import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Box,
  Typography,
  ButtonGroup,
  Button,
  CircularProgress,
} from "@material-ui/core";
import "./chartConfig";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { TooltipItem } from "chart.js";
import { getCreatorTransactionVolumeAPI } from "../../../../api/dashbordApi";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chartContainer: {
      height: "300px",
      width: "100%",
      position: "relative",
      backgroundColor: "#1a1a1a",
      borderRadius: "8px",
      padding: "16px",
      [theme.breakpoints.down("md")]: {
        height: "250px",
      },
      [theme.breakpoints.down("sm")]: {
        height: "200px",
      },
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    title: {
      color: "#FFFFFF",
      fontSize: "18px",
      fontWeight: 600,
    },
    buttonGroup: {
      boxShadow: "none",
      "& .MuiButtonGroup-grouped:not(:last-of-type)": {
        borderRightColor: "#FE5A97",
      },
      "& .MuiButton-root": {
        textTransform: "none",
        fontWeight: 600,
        fontSize: "14px",
        boxShadow: "none",
        padding: "6px 14px",
        minWidth: "72px",
      },
      "& .MuiButton-contained": {
        backgroundColor: "#FE5A97",
        borderColor: "#FE5A97",
        color: "#FFFFFF",
        "&:hover": {
          backgroundColor: "#e54d88",
          borderColor: "#e54d88",
          boxShadow: "none",
        },
      },
      "& .MuiButton-outlined": {
        color: "#FFFFFF",
        borderColor: "#FE5A97",
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: "rgba(254, 90, 151, 0.08)",
          borderColor: "#FE5A97",
        },
      },
    },
    loadingContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      color: "#FFFFFF",
    },
    errorContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      color: "#FF6B6B",
    },
  })
);

export interface ITransactionVolumeData {
  timestamp: string;
  count: number;
  volume: number; // Total transaction value
  tips: number;
  subscriptions: number;
  customVideos: number;
  posts: number;
}

type TransactionVolumeChartProps = {
  className?: string;
};

const TransactionVolumeChart: React.FC<TransactionVolumeChartProps> = ({
  className,
}) => {
  const classes = useStyles();
  const [viewType, setViewType] = useState<"24h" | "7d">("24h");
  const [data, setData] = useState<ITransactionVolumeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (period: "24h" | "7d") => {
    setLoading(true);
    setError(null);

    try {
      const response = await (getCreatorTransactionVolumeAPI as any)({
        period,
        granularity: period === "24h" ? "hourly" : "daily",
        fillGaps: true,
      });

      if (response && response.data) {
        setData(response.data);
      } else {
        setError("No transaction data available");
      }
    } catch (err) {
      console.error("Error fetching transaction volume data:", err);
      setError("Failed to load transaction data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(viewType);
  }, [viewType]);

  const handleViewChange = (newView: "24h" | "7d") => {
    setViewType(newView);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#FFFFFF",
          boxWidth: 14,
          padding: 16,
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#FFFFFF",
        bodyColor: "#FFFFFF",
        borderColor: "#FE5A97",
        borderWidth: 1,
        callbacks: {
          label: (context: TooltipItem<"line">) => {
            const value = context?.parsed?.y;
            if (context?.dataset?.label === "Transaction Count") {
              return `${context?.dataset?.label}: ${value} transactions`;
            } else {
              return `${context?.dataset?.label}: $${
                value?.toLocaleString() || 0
              }`;
            }
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: "#FFFFFF",
          maxTicksLimit: 8,
        },
        grid: {
          color: "#2F2F2F",
          drawBorder: false,
        },
      },
      y: {
        ticks: {
          color: "#FFFFFF",
          callback: (value: string | number) => {
            const val = Number(value);
            if (val >= 1000) {
              return `${(val / 1000).toFixed(1)}K`;
            }
            return val.toString();
          },
        },
        grid: {
          color: "#2F2F2F",
          drawBorder: false,
        },
        beginAtZero: true,
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    },
  };

  const chartData = {
    labels: data.map(item => {
      const date = new Date(item.timestamp);
      if (viewType === "24h") {
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
        });
      } else {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }
    }),
    datasets: [
      {
        label: "Transaction Count",
        data: data.map(item => item.count),
        borderColor: "#FE5A97",
        backgroundColor: "rgba(254, 90, 151, 0.1)",
        fill: false,
        tension: 0.4,
        yAxisID: "y",
      },
      {
        label: "Transaction Volume ($)",
        data: data.map(item => item.volume),
        borderColor: "#FFD700",
        backgroundColor: "rgba(255, 215, 0, 0.1)",
        fill: false,
        tension: 0.4,
        yAxisID: "y",
      },
    ],
  };

  if (loading) {
    return (
      <Box className={`${classes.chartContainer} ${className || ""}`}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} style={{ color: "#FE5A97" }} />
          <Typography style={{ marginLeft: "12px" }}>
            Loading transaction data...
          </Typography>
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={`${classes.chartContainer} ${className || ""}`}>
        <div className={classes.errorContainer}>
          <Typography>{error}</Typography>
        </div>
      </Box>
    );
  }

  return (
    <Box className={`${classes.chartContainer} ${className || ""}`}>
      <div className={classes.header}>
        <Typography className={classes.title}>Transaction Volume</Typography>
        <ButtonGroup
          className={classes.buttonGroup}
          size="small"
          variant="outlined"
        >
          <Button
            onClick={() => handleViewChange("24h")}
            variant={viewType === "24h" ? "contained" : "outlined"}
          >
            Last 24h
          </Button>
          <Button
            onClick={() => handleViewChange("7d")}
            variant={viewType === "7d" ? "contained" : "outlined"}
          >
            Last 7d
          </Button>
        </ButtonGroup>
      </div>

      {data.length > 0 ? (
        <Line data={chartData} options={chartOptions} />
      ) : (
        <div className={classes.loadingContainer}>
          <Typography>No transaction data available</Typography>
        </div>
      )}
    </Box>
  );
};

export default TransactionVolumeChart;
